import React, { useEffect, useState } from 'react';
import {
  addAllowedEmail,
  getAllowedEmails,
  getDashboardStats,
  getStatsAccess,
  removeAllowedEmail,
  setAlphaTesterAchievement,
} from '../api';

function AlphaTesterPanel({ userId, enabled, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const handleToggle = async () => {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      const next = !enabled;
      const result = await setAlphaTesterAchievement(userId, next);
      onChange(Boolean(result.alphaTesterEnabled));
      if (result.alphaTesterEnabled) {
        setNotice(
          `Alpha Tester is on. Awarded to ${result.newlyAwarded || 0} of ${
            result.activeUsers || 0
          } active accounts (already earned stays earned).`
        );
      } else {
        setNotice(
          'Alpha Tester is off. New active accounts will not earn it. Existing unlocks stay.'
        );
      }
    } catch (err) {
      setError(err.error || 'Unable to update Alpha Tester setting');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dashboard-section">
      <h2>Alpha Tester achievement</h2>
      <p className="subtitle">
        While this is on, every active account earns the Alpha Tester badge. Turn it off when alpha
        ends — people who already unlocked it keep it.
      </p>
      {error ? <div className="error">{error}</div> : null}
      {notice ? <div className="success">{notice}</div> : null}
      <div className="alpha-tester-row">
        <span className={`alpha-tester-status${enabled ? ' on' : ''}`}>
          Status: {enabled ? 'Enabled' : 'Disabled'}
        </span>
        <button type="button" className={enabled ? 'secondary' : undefined} disabled={busy} onClick={handleToggle}>
          {busy ? 'Saving...' : enabled ? 'Disable Alpha Tester' : 'Enable Alpha Tester'}
        </button>
      </div>
    </section>
  );
}

function ApprovedEmailsPanel({ userId }) {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!userId) return;
    getAllowedEmails(userId)
      .then((data) => setEmails(data.emails || []))
      .catch((err) => setError(err.error || 'Unable to load approved emails'));
  }, [userId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setBusy(true);
    try {
      const result = await addAllowedEmail(userId, newEmail.trim());
      setEmails(result.emails || []);
      setNewEmail('');
      setNotice(result.created ? 'Email added to the approved list.' : 'That email was already approved.');
    } catch (err) {
      setError(err.error || 'Unable to add email');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (email) => {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      const result = await removeAllowedEmail(userId, email);
      setEmails(result.emails || []);
      setNotice('Email removed from the approved list.');
    } catch (err) {
      setError(err.error || 'Unable to remove email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="dashboard-section">
      <h2>Approved registration emails</h2>
      <p className="subtitle">
        Only these emails can create an account. Add someone here before they register.
      </p>
      {error ? <div className="error">{error}</div> : null}
      {notice ? <div className="success">{notice}</div> : null}
      <form className="approved-email-form" onSubmit={handleAdd}>
        <div className="form-group">
          <label htmlFor="approvedEmail">Add email</label>
          <input
            id="approvedEmail"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="name@example.com"
            required
          />
        </div>
        <button type="submit" disabled={busy}>
          {busy ? 'Saving...' : 'Add'}
        </button>
      </form>
      <ul className="approved-email-list">
        {emails.map((row) => (
          <li key={row.id || row.email}>
            <span>{row.email}</span>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => handleRemove(row.email)}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      {!emails.length ? <p className="subtitle">No approved emails yet.</p> : null}
    </section>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="stat-card">
      <span className="stat-label">{label}</span>
      <strong className="stat-value">{value}</strong>
      {hint ? <span className="stat-hint">{hint}</span> : null}
    </div>
  );
}

function CountTable({ title, rows, emptyLabel = 'No data yet.' }) {
  if (!rows?.length) {
    return (
      <section className="dashboard-panel">
        <h3>{title}</h3>
        <p className="subtitle">{emptyLabel}</p>
      </section>
    );
  }
  const max = Math.max(...rows.map((row) => row.count));
  return (
    <section className="dashboard-panel">
      <h3>{title}</h3>
      <ul className="count-table">
        {rows.map((row) => (
          <li key={row.label}>
            <span className="count-label">{row.label}</span>
            <div className="count-bar-wrap">
              <div className="count-bar" style={{ width: `${Math.max(6, (row.count / max) * 100)}%` }} />
            </div>
            <span className="count-value">{row.count}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SideEffectTable({ rows }) {
  return (
    <section className="dashboard-panel">
      <h3>Side effect rates</h3>
      <ul className="count-table side-effect-table">
        {(rows || []).map((row) => (
          <li key={row.key}>
            <span className="count-label">{row.label}</span>
            <div className="count-bar-wrap">
              <div className="count-bar side-effect-bar" style={{ width: `${Math.max(6, row.rate)}%` }} />
            </div>
            <span className="count-value">
              {row.count} ({row.rate}%)
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StyleSection({ style, data }) {
  if (!data?.count) {
    return (
      <section className="dashboard-panel style-panel">
        <h3>{style}</h3>
        <p className="subtitle">No sessions logged for this style yet.</p>
      </section>
    );
  }

  return (
    <section className="dashboard-panel style-panel">
      <h3>
        {style} <span className="panel-count">{data.count} sessions</span>
      </h3>
      <div className="style-grid">
        {data.howMethod?.length ? <CountTable title="How / type" rows={data.howMethod} /> : null}
        {data.howStyle?.length ? <CountTable title="Style" rows={data.howStyle} /> : null}
        {data.amount?.length ? <CountTable title="Amount" rows={data.amount} /> : null}
        {data.strength?.length ? <CountTable title="Strength (top values)" rows={data.strength} /> : null}
        {data.enjoyIt && (data.enjoyIt.yes || data.enjoyIt.no) ? (
          <section className="dashboard-panel nested-panel">
            <h3>Enjoyed it</h3>
            <ul className="count-table compact">
              <li>
                <span className="count-label">Yes</span>
                <span className="count-value">{data.enjoyIt.yes}</span>
              </li>
              <li>
                <span className="count-label">No</span>
                <span className="count-value">{data.enjoyIt.no}</span>
              </li>
            </ul>
          </section>
        ) : null}
        {data.buyAgain?.average != null ? (
          <section className="dashboard-panel nested-panel">
            <h3>Buy again</h3>
            <p className="subtitle">Average score: {data.buyAgain.average}</p>
            <CountTable title="Score distribution" rows={data.buyAgain.distribution} />
          </section>
        ) : null}
        {data.topStrains?.length ? <CountTable title="Top strains" rows={data.topStrains} /> : null}
        {data.topBrands?.length ? <CountTable title="Top brands" rows={data.topBrands} /> : null}
        {data.strainTypes?.length ? <CountTable title="Strain types" rows={data.strainTypes} /> : null}
        {data.groupSize?.length ? <CountTable title="Group size" rows={data.groupSize} /> : null}
        <SideEffectTable rows={data.sideEffects} />
      </div>
      <p className="field-note">
        Alone: {data.alone.yes} · With others: {data.alone.no} · With notes: {data.withNotes}
      </p>
    </section>
  );
}

function Dashboard({ user, onBack }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [alphaTesterEnabled, setAlphaTesterEnabled] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    setAccessChecked(false);
    getStatsAccess(user.id)
      .then((data) => setAllowed(Boolean(data.allowed)))
      .catch(() => setAllowed(false))
      .finally(() => setAccessChecked(true));
  }, [user]);

  useEffect(() => {
    if (!user?.id || !allowed) return;
    setLoading(true);
    getDashboardStats(user.id)
      .then((data) => {
        setStats(data.stats || null);
        setAlphaTesterEnabled(data.alphaTesterEnabled !== false);
        setError('');
      })
      .catch((err) => {
        setStats(null);
        setError(err.error || 'Unable to load dashboard');
      })
      .finally(() => setLoading(false));
  }, [user, allowed]);

  if (!accessChecked) {
    return (
      <div className="container wide">
        <p className="brand">My Weed Buddy</p>
        <h1>Stats dashboard</h1>
        <p className="subtitle">Loading...</p>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="container wide">
        <p className="brand">My Weed Buddy</p>
        <h1>Stats dashboard</h1>
        <p className="subtitle">This dashboard is not available for your account.</p>
        <button type="button" className="secondary" onClick={onBack}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="container dashboard">
      <p className="brand">My Weed Buddy</p>
      <h1>Stats dashboard</h1>
      <p className="subtitle">
        Aggregated session and profile data across all active users. Names and email addresses are
        not shown.
      </p>
      <button type="button" className="secondary" onClick={onBack}>
        Back to welcome
      </button>

      <ApprovedEmailsPanel userId={user.id} />
      <AlphaTesterPanel
        userId={user.id}
        enabled={alphaTesterEnabled}
        onChange={setAlphaTesterEnabled}
      />

      {loading ? <p className="subtitle">Loading dashboard...</p> : null}
      {error ? <div className="error">{error}</div> : null}

      {stats ? (
        <>
          <section className="dashboard-section">
            <h2>Overview</h2>
            <div className="stat-grid">
              <StatCard label="Active users" value={stats.overview.activeUsers} />
              <StatCard label="Total sessions" value={stats.overview.totalSessions} />
              <StatCard label="Sessions with notes" value={stats.overview.sessionsWithNotes} />
              <StatCard
                label="Average buy again"
                value={stats.overview.averageBuyAgain ?? '—'}
                hint="1 = never, 5 = always"
              />
            </div>
          </section>

          <section className="dashboard-section">
            <h2>All sessions</h2>
            <div className="dashboard-grid">
              <CountTable title="By consumption style" rows={stats.sessions.byStyle} />
              <CountTable title="Buy again scores" rows={stats.sessions.buyAgain} />
              <CountTable title="Strain types" rows={stats.sessions.strainTypes} />
              <CountTable title="Top strains" rows={stats.sessions.topStrains} />
              <CountTable title="Top brands" rows={stats.sessions.topBrands} />
              <CountTable title="Starting moods" rows={stats.sessions.startingMoods} />
              <CountTable title="Ending moods" rows={stats.sessions.endingMoods} />
              <CountTable title="Sessions by month" rows={stats.sessions.byMonth} />
              <SideEffectTable rows={stats.sessions.sideEffects} />
              <section className="dashboard-panel">
                <h3>Social context</h3>
                <ul className="count-table compact">
                  <li>
                    <span className="count-label">Alone</span>
                    <span className="count-value">{stats.sessions.alone.yes}</span>
                  </li>
                  <li>
                    <span className="count-label">With others</span>
                    <span className="count-value">{stats.sessions.alone.no}</span>
                  </li>
                </ul>
                <CountTable title="Group size" rows={stats.sessions.groupSize} />
              </section>
            </div>
          </section>

          <section className="dashboard-section">
            <h2>People (aggregated)</h2>
            <p className="subtitle">Profile fields only — no names or emails.</p>
            <div className="dashboard-grid">
              <CountTable title="Gender" rows={stats.demographics.gender} />
              <CountTable title="Race" rows={stats.demographics.race} />
              <CountTable title="Age range" rows={stats.demographics.ageBuckets} />
              <CountTable title="Preferred consumption style" rows={stats.demographics.profileConsumptionStyle} />
              <CountTable title="Preferred strain type" rows={stats.demographics.profileStrainType} />
              <CountTable title="Zip prefix (first 3 digits)" rows={stats.demographics.zipPrefix} />
            </div>
          </section>

          <section className="dashboard-section">
            <h2>By consumption style</h2>
            {Object.entries(stats.byConsumptionStyle || {}).map(([style, data]) => (
              <StyleSection key={style} style={style} data={data} />
            ))}
          </section>
        </>
      ) : null}
    </div>
  );
}

export default Dashboard;
